import { memo, Suspense, useEffect, useLayoutEffect, useRef, type ComponentType, type RefObject } from 'react';
import { TAB_IDS, tabDestination, tabIndex, type TabId } from './destinations';
import { focusIsStranded, restoreFocus } from './focus';
import { LayerErrorBoundary } from './LayerErrorBoundary';
import type { NavLocation } from './navigator';
import { PaneActiveContext } from './pane';
import { PaneRoutes, TAB_SCREENS } from './screens';
import styles from './TabStack.module.css';

export interface TabStackProps {
  activeTab: TabId;
  /** Each visited tab's own location; unvisited tabs are not mounted yet. */
  locations: Partial<Record<TabId, NavLocation>>;
  /** An overlay or a pushed page covers the stack: inert and hidden from assistive tech. */
  hidden: boolean;
  /**
   * The stack still paints (default: not `hidden`). A cover keeps it visible
   * while it slides over, so the pane underneath can shift with it, and hides
   * it once the transition is done.
   */
  visible?: boolean;
  /** Nothing is layered above the shell. */
  interactive: boolean;
  /** Desktop layout: tabs switch at once (sidebar destinations), without the slide. */
  instant?: boolean;
}

interface TabPaneProps {
  location: NavLocation;
  Screen: ComponentType;
  active: boolean;
}

const TabPane = memo(function TabPane({ location, Screen, active }: TabPaneProps) {
  return (
    <PaneActiveContext value={active}>
      <LayerErrorBoundary>
        <Suspense fallback={<div className={styles.fallback} aria-busy="true" />}>
          <PaneRoutes location={location} Screen={Screen} />
        </Suspense>
      </LayerErrorBoundary>
    </PaneActiveContext>
  );
});

/**
 * Keeps focus with the visible pane: when the active pane changes or the stack
 * is revealed while focus was left behind in an inert pane (a Menu row that
 * switched tabs) or dropped (a closed overlay), focus moves to where the user
 * last was in the pane now shown, or to the pane itself.
 */
function usePaneFocus(rootRef: RefObject<HTMLDivElement | null>, activeTab: TabId, hidden: boolean): void {
  const memory = useRef(new Map<TabId, Element>());
  const previous = useRef({ activeTab, hidden });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof Element)) return;
      const tab = event.target.closest<HTMLElement>('[data-tab]')?.dataset.tab;
      if (tab) memory.current.set(tab as TabId, event.target);
    };
    root.addEventListener('focusin', onFocusIn);
    return () => root.removeEventListener('focusin', onFocusIn);
  }, [rootRef]);

  useLayoutEffect(() => {
    const was = previous.current;
    previous.current = { activeTab, hidden };
    const shownAgain = was.hidden && !hidden;
    if (hidden || (!shownAgain && was.activeTab === activeTab)) return;
    const pane = rootRef.current?.querySelector<HTMLElement>(`[data-tab="${activeTab}"]`);
    if (pane && focusIsStranded(pane)) restoreFocus(pane, memory.current.get(activeTab) ?? null);
  }, [rootRef, activeTab, hidden]);
}

/**
 * Visited tabs stay mounted. Every pane animates from where it is to its
 * target (active 0, before the active tab −100 %, after it +100 %), so a first
 * visit slides in from its side. Inactive panes are inert and hidden once
 * they have slid away, which also keeps intermediate panes from sweeping
 * across the screen on multi-step jumps. A stack revealed by an overlay that
 * closed onto another tab slides that tab in too (polish pass; Flutter
 * rebuilds the stack without the slide).
 */
export function TabStack({ activeTab, locations, hidden, visible = !hidden, interactive, instant = false }: TabStackProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  usePaneFocus(rootRef, activeTab, hidden);
  const activeIndex = tabIndex(activeTab);
  return (
    <div
      ref={rootRef}
      className={[styles.stack, visible ? '' : styles.hidden, instant ? styles.instant : ''].filter(Boolean).join(' ')}
      inert={hidden}
      aria-hidden={hidden || undefined}
    >
      {TAB_IDS.map((tab, index) => {
        const active = index === activeIndex;
        const offset = active ? 0 : index < activeIndex ? -100 : 100;
        const location = locations[tab];
        return (
          <section
            key={tab}
            className={[styles.pane, active ? styles.activePane : ''].filter(Boolean).join(' ')}
            style={instant ? undefined : { transform: `translate3d(${offset}%, 0, 0)` }}
            data-tab={tab}
            // The shown pane is the screen's main landmark; the others are inert and hidden.
            role={active && !hidden ? 'main' : undefined}
            tabIndex={-1}
            inert={!active}
            aria-hidden={!active || undefined}
            aria-label={tabDestination(tab).label}
          >
            {location && <TabPane location={location} Screen={TAB_SCREENS[tab]} active={active && !hidden && interactive} />}
          </section>
        );
      })}
    </div>
  );
}
